import type {
  ExtraditionMlaSaveStep1,
  ExtraditionMlaSaveStep2,
  ExtraditionMlaSaveStep3,
  ExtraditionMlaSaveStep4,
  ExtraditionMlaSaveStep5,
  ExtraditionMlaSaveStep6,
  ExtraditionMlaSaveStep6File,
  ExtraditionMlaStepsResponse,
  SaveExtraditionMlaStepsPayload,
} from '../types/extraditionMlaApi';
import type { ExtraditionMlaDocumentRow, ExtraditionMlaWizardDraft } from '../types/extraditionMlaCaseRequest';
import {
  draftHasOtherDeliverySelected,
} from '../utils/deliveryMethod';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import { requiresCustodialAuthority } from '../pages/wizard/extraditionMla/extraditionMlaHelpers';
import { FILE_CATEGORY_EXTRADITION_MLA, type ExtraditionMlaWizardStep } from '../pages/wizard/extraditionMla/extraditionMlaConstants';
import {
  parseApplicableAreasFromApi,
  parsePhysicalFileDeliveryMethods,
} from './criminalCaseMapper';

export type { ExtraditionMlaWizardStep };

export function caseRequestIdFromResponse(response: ExtraditionMlaStepsResponse): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: ExtraditionMlaStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function step1LooksComplete(s1: ExtraditionMlaSaveStep1 | undefined): boolean {
  return Boolean(
    (s1?.sub_category_id ?? '').trim() &&
      (s1?.description ?? '').trim() &&
      (s1?.case_source ?? '').trim() &&
      (s1?.country_involved_id ?? '').trim() &&
      (s1?.province_id ?? '').trim() &&
      (s1?.external_case_reference_number ?? '').trim(),
  );
}

function step2LooksComplete(s2: ExtraditionMlaSaveStep2 | undefined): boolean {
  const base = Boolean(
    accusedNameFromStep2(s2) &&
      (s2?.nationality_id ?? '').trim() &&
      (s2?.country_of_origin_id ?? '').trim() &&
      (s2?.date_of_birth ?? '').trim() &&
      (s2?.gender_id ?? '').trim() &&
      (s2?.current_custody_status_id ?? '').trim(),
  );
  if (!base) return false;

  const statusName = (s2?.current_custody_status_name ?? '').trim();
  const statusId = (s2?.current_custody_status_id ?? '').trim();
  if (requiresCustodialAuthority(statusName, statusId)) {
    return Boolean((s2?.custodial_authority ?? '').trim());
  }
  return true;
}

function step3LooksComplete(s3: ExtraditionMlaSaveStep3 | undefined): boolean {
  const warrantAvailable = arrestWarrantFromStep3(s3);
  if (warrantAvailable == null) return false;
  if (warrantAvailable) {
    return Boolean((s3?.warrant_reference ?? '').trim());
  }
  return true;
}

function step4LooksComplete(s4: ExtraditionMlaSaveStep4 | undefined): boolean {
  return Boolean((s4?.urgency_level_id ?? '').trim());
}

function step5LooksComplete(s5: ExtraditionMlaSaveStep5 | undefined): boolean {
  return Boolean((s5?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step6LooksComplete(s6: ExtraditionMlaSaveStep6 | undefined): boolean {
  const files = step6DocumentsFromApi(s6);
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

export function resolveExtraditionMlaDraftResumeStep(
  response: ExtraditionMlaStepsResponse,
): ExtraditionMlaWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  if (response.step1Processed === false) return 1;
  if (response.step2Processed === false) return 2;
  if (response.step3Processed === false) return 3;
  if (response.step4Processed === false) return 4;
  if (response.step5Processed === false) return 5;
  if (response.step6Processed === false) return 6;

  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;
  const s6 = response.step_6;

  if (!step1LooksComplete(s1)) return 1;
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete(s3)) return 3;
  if (!step4LooksComplete(s4)) return 4;
  if (!step5LooksComplete(s5)) return 5;
  if (!step6LooksComplete(s6)) return 6;
  return 6;
    },
    (step) => step >= 1 && step <= 6,
  );
}

function apiDocumentTypeId(file: ExtraditionMlaSaveStep6File): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function step6DocumentsFromApi(s6: ExtraditionMlaSaveStep6 | undefined): ExtraditionMlaSaveStep6File[] {
  return [
    ...(s6?.uploaded_files ?? []),
    ...(s6?.extra_uploaded_files ?? []),
    ...(s6?.documents ?? []),
  ];
}

function mapApiString(value: string | null | undefined, fallback: string): string {
  if (value === null) return '';
  if (value === undefined) return fallback;
  return value;
}

function trimmedOrUndefined(v: string | undefined | null): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

function accusedNameFromStep2(s2: ExtraditionMlaSaveStep2 | undefined): string | undefined {
  return trimmedOrUndefined(s2?.name_of_accused) ?? trimmedOrUndefined(s2?.accused_name);
}

function passportFromStep2(s2: ExtraditionMlaSaveStep2 | undefined): string | undefined {
  return trimmedOrUndefined(s2?.passport_or_nic) ?? trimmedOrUndefined(s2?.passport_or_id_number);
}

function arrestWarrantFromStep3(s3: ExtraditionMlaSaveStep3 | undefined): boolean | null | undefined {
  if (s3?.is_arrest_warrant_available != null) return s3.is_arrest_warrant_available;
  return s3?.arrest_warrant_available;
}

function treatyArrangementFromStep3(s3: ExtraditionMlaSaveStep3 | undefined): string | undefined {
  return trimmedOrUndefined(s3?.applicable_treaty_or_arrangement) ?? trimmedOrUndefined(s3?.applicable_treaty_arrangement);
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

type SavedFileRow = {
  file_name?: string | null;
  original_file_name?: string | null;
  fileName?: string | null;
  originalFileName?: string | null;
  upload_time?: string | null;
  uploadTime?: string | null;
};

function savedFileFields(file: SavedFileRow): { fileName: string; originalFileName: string } {
  const apiFileName = file.file_name ?? file.fileName ?? '';
  const original = file.original_file_name ?? file.originalFileName;
  return normalizeDocumentFileFields(apiFileName, original);
}

function step6FileFields(file: ExtraditionMlaSaveStep6File): { fileName: string; originalFileName: string } {
  return savedFileFields(file);
}

function step6FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function otherMethodFromStep5(s5: ExtraditionMlaSaveStep5 | undefined): string {
  const raw = s5?.other_method ?? s5?.otherMethod;
  return typeof raw === 'string' ? raw.trim() : '';
}

function otherMethodFromDeliveryRows(s5: ExtraditionMlaSaveStep5 | undefined): string {
  const raw = s5?.physical_file_delivery_methods;
  if (!raw?.length || typeof raw[0] === 'string') return '';

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const name = item.delivery_method_name?.trim() ?? '';
    const other = item.other_method?.trim();
    if (name.toLowerCase() === 'other' && other) return other;
  }
  return '';
}

function resolveApplicableAreaIds(response: ExtraditionMlaStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  return prev;
}

function resolveApplicableAreaNames(response: ExtraditionMlaStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function mapStep1(draft: ExtraditionMlaWizardDraft): SaveStepFields<ExtraditionMlaSaveStep1> {
  return {
    category_id: strOrNull(draft.fileCategoryId),
    description: strOrNull(draft.description),
    case_source: strOrNull(draft.caseSource),
    country_involved_id: strOrNull(draft.countryInvolvedId),
    country_involved_name: strOrNull(draft.countryInvolvedName),
    direction_of_request: strOrNull(draft.directionOfRequest),
    sub_category_id: strOrNull(draft.subFileCategoryId),
    sub_category_name: strOrNull(draft.subFileCategoryName),
    province_id: strOrNull(draft.provinceId),
    province_name: strOrNull(draft.provinceName),
    district_id: strOrNull(draft.districtId),
    district_name: strOrNull(draft.districtName),
    external_case_reference_number: strOrNull(draft.yourReferenceNumber),
    other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: yesNoToBool(draft.hasConnectedMatters),
    connected_case_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_available:
      draft.hasConnectedMatters === 'yes' ? draft.connectedAgdFileRefAvailable : undefined,
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' && draft.connectedAgdFileRefAvailable
        ? strOrNull(draft.connectedAgdFileRef)
        : undefined,
  };
}

function mapStep2(draft: ExtraditionMlaWizardDraft): SaveStepFields<ExtraditionMlaSaveStep2> {
  return {
    name_of_accused: strOrNull(draft.accusedName),
    nationality_id: strOrNull(draft.nationalityId),
    nationality_name: strOrNull(draft.nationalityName),
    country_of_origin_id: strOrNull(draft.countryOfOriginId),
    country_of_origin_name: strOrNull(draft.countryOfOriginName),
    date_of_birth: strOrNull(draft.accusedDob),
    gender_id: strOrNull(draft.accusedGenderId),
    gender_name: strOrNull(draft.accusedGenderName),
    passport_or_nic: strOrNull(draft.passportOrIdNumber),
    current_custody_status_id: strOrNull(draft.custodyStatusId),
    current_custody_status_name: strOrNull(draft.custodyStatusName),
    custodial_authority: strOrNull(draft.custodialAuthority),
    last_known_location: strOrNull(draft.lastKnownLocation),
  };
}

function mapStep3(draft: ExtraditionMlaWizardDraft): SaveStepFields<ExtraditionMlaSaveStep3> {
  return {
    is_arrest_warrant_available: yesNoToBool(draft.arrestWarrantAvailable),
    warrant_reference:
      draft.arrestWarrantAvailable === 'yes' ? strOrNull(draft.warrantReference) : undefined,
    applicable_treaty_or_arrangement: strOrNull(draft.applicableTreatyArrangement),
  };
}

function mapStep4(draft: ExtraditionMlaWizardDraft): SaveStepFields<ExtraditionMlaSaveStep4> {
  return {
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    urgency_level_name: strOrNull(draft.urgencyLevelName),
    deadline: strOrNull(draft.deadline),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep5(draft: ExtraditionMlaWizardDraft): SaveStepFields<ExtraditionMlaSaveStep5> {
  const ids = draft.deliveryMethodIds.filter(Boolean);
  const step5: SaveStepFields<ExtraditionMlaSaveStep5> = {
    physical_file_delivery_method_ids: ids.length ? ids : undefined,
    submitted_by_user_id: strOrNull(draft.submittedByUserId),
    submitted_by_display_name: strOrNull(draft.submittedByDisplayName),
    submitted_division_name: strOrNull(draft.submittedDivisionName),
    designation: strOrNull(draft.submitterDesignation),
    email: strOrNull(draft.submitterEmail),
    contact_no: strOrNull(draft.submitterContactNo),
  };

  if (draftHasOtherDeliverySelected(draft)) {
    step5.other_method = strOrNull(draft.deliveryOther);
  }

  return step5;
}

function mapStep6(draft: ExtraditionMlaWizardDraft): SaveStepFields<ExtraditionMlaSaveStep6> {
  const uploaded_files = draft.documents
    .filter(
      (d) =>
        isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step6FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
      return {
        ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  const extra_uploaded_files = draft.documents
    .filter(
      (d) =>
        !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step6FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return {
        ...names,
        ...(reason ? { reason } : {}),
      };
    });

  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

export function buildSaveExtraditionMlaPayload(
  draft: ExtraditionMlaWizardDraft,
  options: {
    currentStep?: ExtraditionMlaWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveExtraditionMlaStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SaveExtraditionMlaStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    payload.step_1 = mapStep1(draft);
    payload.step_2 = mapStep2(draft);
    payload.step_3 = mapStep3(draft);
    payload.step_4 = mapStep4(draft);
    payload.step_5 = mapStep5(draft);
    payload.step_6 = mapStep6(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 3) payload.step_3 = mapStep3(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    if (step >= 5) payload.step_5 = mapStep5(draft);
    if (step >= 6) payload.step_6 = mapStep6(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) payload.step_3 = mapStep3(draft);
  if (step === 4) payload.step_4 = mapStep4(draft);
  if (step === 5) payload.step_5 = mapStep5(draft);
  if (step === 6) payload.step_6 = mapStep6(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapExtraditionMlaStepsToDraft(
  response: ExtraditionMlaStepsResponse,
  prev: ExtraditionMlaWizardDraft,
): ExtraditionMlaWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;
  const s6 = response.step_6;

  type LegacyStep2 = ExtraditionMlaSaveStep2 & {
    arrest_warrant_available?: boolean | null;
    is_arrest_warrant_available?: boolean | null;
    warrant_reference?: string | null;
    applicable_treaty_arrangement?: string | null;
    applicable_treaty_or_arrangement?: string | null;
  };

  type LegacyTimelineFields = {
    urgency_level_id?: string;
    urgency_level_name?: string;
    deadline?: string | null;
    urgency_justification?: string | null;
  };

  type LegacyDeliveryFields = {
    physical_file_delivery_method_ids?: string[];
    physical_file_delivery_methods?: ExtraditionMlaSaveStep5['physical_file_delivery_methods'];
    other_method?: string | null;
    otherMethod?: string | null;
  };

  const legacyS2 = s2 as LegacyStep2 | undefined;
  const legacyTimeline = (s4?.urgency_level_id ? s4 : s3) as LegacyTimelineFields | undefined;
  const legacyDelivery = (s5?.physical_file_delivery_method_ids?.length ? s5 : s4) as
    | LegacyDeliveryFields
    | undefined;
  const legacyDocs = s6 ?? (response.step_5 as ExtraditionMlaSaveStep6 | undefined);

  const documents = (() => {
    const fromApi = step6DocumentsFromApi(legacyDocs);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      return fromApi
        .filter((f) => {
          const typeId = apiDocumentTypeId(f);
          return typeId ? isDocumentTypeUuid(typeId) : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        })
        .map((f) => {
          const { fileName, originalFileName } = step6FileFields(f);
          const typeId = apiDocumentTypeId(f);
          return {
            id: (f.case_request_document_id ?? '').trim() || crypto.randomUUID(),
            checklistItemId: '',
            documentTypeId: typeId,
            documentName: f.document_type_name?.trim() || 'Document',
            mandatory: false,
            fileName,
            originalFileName,
            uploadTime: f.upload_time ?? '',
            skipReason: f.reason ?? '',
          };
        });
    }
    return prev.documents.map((doc) => {
      const match = fromApi.find((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        if (
          !isDocumentTypeUuid(doc.documentTypeId) &&
          f.document_type_name &&
          doc.documentName &&
          f.document_type_name.trim().toLowerCase() === doc.documentName.trim().toLowerCase()
        ) {
          return true;
        }
        return false;
      });
      if (!match) return doc;
      const { fileName, originalFileName } = step6FileFields(match);
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(match) || doc.documentTypeId,
        fileName: fileName || doc.fileName,
        originalFileName: originalFileName || doc.originalFileName,
        uploadTime: match.upload_time ?? doc.uploadTime,
        skipReason: match.reason ?? doc.skipReason,
      };
    });
  })();

  const delivery = parsePhysicalFileDeliveryMethods(legacyDelivery?.physical_file_delivery_methods);
  const deliveryIds = legacyDelivery?.physical_file_delivery_method_ids?.length
    ? legacyDelivery.physical_file_delivery_method_ids
    : delivery.ids;

  const fileCategoryName =
    (response.file_category_name ?? s1?.category_name ?? prev.fileCategory) || FILE_CATEGORY_EXTRADITION_MLA;

  return {
    ...prev,
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategory: fileCategoryName,
    fileCategoryId: response.file_category_id ?? s1?.category_id ?? prev.fileCategoryId,
    subFileCategoryId: response.file_sub_category_id ?? s1?.sub_category_id ?? prev.subFileCategoryId,
    subFileCategoryName: response.file_sub_category_name ?? s1?.sub_category_name ?? prev.subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    description: mapApiString(s1?.description, prev.description),
    caseSource: mapApiString(s1?.case_source, prev.caseSource),
    countryInvolvedId: mapApiString(s1?.country_involved_id, prev.countryInvolvedId),
    countryInvolvedName: mapApiString(s1?.country_involved_name, prev.countryInvolvedName),
    directionOfRequest: mapApiString(s1?.direction_of_request, prev.directionOfRequest),
    provinceId: mapApiString(s1?.province_id, prev.provinceId),
    provinceName: mapApiString(s1?.province_name, prev.provinceName),
    districtId: mapApiString(s1?.district_id, prev.districtId),
    districtName: mapApiString(s1?.district_name, prev.districtName),
    yourReferenceNumber: mapApiString(s1?.external_case_reference_number, prev.yourReferenceNumber),
    otherExternalReferences: mapApiString(s1?.other_external_case_reference_number, prev.otherExternalReferences),
    caseNumber: mapApiString(s1?.case_number, prev.caseNumber),
    hasConnectedMatters:
      s1?.has_connected_case != null
        ? boolToYesNo(s1.has_connected_case)
        : prev.hasConnectedMatters,
    connectedCaseNumber: mapApiString(s1?.connected_case_number, prev.connectedCaseNumber),
    connectedAgdFileRefAvailable: (() => {
      if (s1?.connected_agd_file_ref_available != null) {
        return Boolean(s1.connected_agd_file_ref_available);
      }
      const ref = (s1?.connected_agd_file_ref_number ?? '').trim();
      if (ref) return true;
      return prev.connectedAgdFileRefAvailable;
    })(),
    connectedAgdFileRef: mapApiString(s1?.connected_agd_file_ref_number, prev.connectedAgdFileRef),
    accusedName: mapApiString(accusedNameFromStep2(s2), prev.accusedName),
    nationalityId: mapApiString(s2?.nationality_id, prev.nationalityId),
    nationalityName: mapApiString(s2?.nationality_name, prev.nationalityName),
    countryOfOriginId: mapApiString(s2?.country_of_origin_id, prev.countryOfOriginId),
    countryOfOriginName: mapApiString(s2?.country_of_origin_name, prev.countryOfOriginName),
    accusedDob: mapApiString(s2?.date_of_birth, prev.accusedDob),
    accusedGenderId: mapApiString(s2?.gender_id, prev.accusedGenderId),
    accusedGenderName: mapApiString(s2?.gender_name, prev.accusedGenderName),
    passportOrIdNumber: mapApiString(passportFromStep2(s2), prev.passportOrIdNumber),
    custodyStatusId: mapApiString(s2?.current_custody_status_id, prev.custodyStatusId),
    custodyStatusName: mapApiString(s2?.current_custody_status_name, prev.custodyStatusName),
    custodialAuthority: mapApiString(s2?.custodial_authority, prev.custodialAuthority),
    lastKnownLocation: mapApiString(s2?.last_known_location, prev.lastKnownLocation),
    arrestWarrantAvailable: (() => {
      const fromStep3 = arrestWarrantFromStep3(s3);
      if (fromStep3 != null) return boolToYesNo(fromStep3);
      const fromLegacyStep2 = arrestWarrantFromStep3(legacyS2 as ExtraditionMlaSaveStep3 | undefined);
      if (fromLegacyStep2 != null) return boolToYesNo(fromLegacyStep2);
      return prev.arrestWarrantAvailable;
    })(),
    warrantReference: mapApiString(s3?.warrant_reference ?? legacyS2?.warrant_reference, prev.warrantReference),
    applicableTreatyArrangement: mapApiString(
      treatyArrangementFromStep3(s3) ?? treatyArrangementFromStep3(legacyS2 as ExtraditionMlaSaveStep3 | undefined),
      prev.applicableTreatyArrangement,
    ),
    urgencyLevelId: mapApiString(legacyTimeline?.urgency_level_id, prev.urgencyLevelId),
    urgencyLevelName: mapApiString(legacyTimeline?.urgency_level_name, prev.urgencyLevelName),
    deadline: mapApiString(legacyTimeline?.deadline, prev.deadline),
    urgencyJustification: mapApiString(legacyTimeline?.urgency_justification, prev.urgencyJustification),
    deliveryMethodIds: deliveryIds.length ? deliveryIds : prev.deliveryMethodIds,
    deliveryMethods: delivery.names.length ? delivery.names : prev.deliveryMethods,
    deliveryOther:
      otherMethodFromStep5(legacyDelivery as ExtraditionMlaSaveStep5) ||
      otherMethodFromDeliveryRows(legacyDelivery as ExtraditionMlaSaveStep5) ||
      prev.deliveryOther,
    submittedByUserId: mapApiString(s5?.submitted_by_user_id, prev.submittedByUserId),
    submittedByDisplayName: mapApiString(s5?.submitted_by_display_name, prev.submittedByDisplayName),
    submittedDivisionName: mapApiString(s5?.submitted_division_name, prev.submittedDivisionName),
    submitterDesignation: mapApiString(s5?.designation, prev.submitterDesignation),
    submitterEmail: mapApiString(s5?.email, prev.submitterEmail),
    submitterContactNo: mapApiString(s5?.contact_no, prev.submitterContactNo),
    documents,
  };
}
